import { API } from 'aws-amplify';

import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '../types';
import { CustomersState } from './types';

import { Customer } from '@/models';
import _ from 'lodash';


/**
 * Actions
 *
 * Actions are similar to mutations, the differences being that:
 * - Instead of mutating the state, actions commit mutations.
 * - Actions can contain arbitrary asynchronous operations.
 * - Actions are triggered with the store.dispatch method e.g. `store.dispatch('getSettings')`
 */
 export const actions: ActionTree<CustomersState, RootState> = {

	/**
     * Get all customers
     * 
     * @param {CustomersState} store
     * @param filters
     * @returns All Customers
     */
	 async getCustomers(store: ActionContext<CustomersState, any>): Promise<Customer[]> {
		store.commit('app/addRequest', 'getCustomers', {root: true});

		return API.get('RestAPI', `/customers`, {})
			.then((response) => response.map((x: Partial<Customer>) => new Customer(x)))
			.then((customer: Customer[]) => {
					store.commit('setCustomers', customer);
					return customer;
			})
			.finally(() => store.commit('app/removeRequest', 'getCustomers', {root: true}));
	},

	/**
	 * Get one Customer
	 * 
	 * @param {CustomersState} store 
	 * @param id 
	 * @returns individual Customer
	 */
	 async getCustomer(store: ActionContext<CustomersState, any>, id) {
		// New Customer
		if (id === 'new') {
			const customer = new Customer();
			store.commit('setCurrent', customer);
			return customer;
		}
		store.commit('app/addRequest', 'getCustomer', {root: true});

		return API.get('RestAPI', `/customers/${id}`, null)
			.then((response) => new Customer(response))
			.then((customer: Customer) => {
					store.commit('setCurrent', customer);
					return customer;
			})
			.finally(() => store.commit('app/removeRequest', 'getCustomer', {root: true}));
	},

	/**
   * Add Customer
   * 
   * @param {CustomersState} store 
   * @param customer Customer
   */
	 async addCustomer(store: ActionContext<CustomersState, any>, customer: Customer): Promise<void> {
		store.commit('app/addRequest', 'addCustomer', {root: true});
		return API.post('RestAPI', '/customers', {body: customer.toJSON()})
			.then((data) => new Customer(data))
			.then((customerfromserver: Customer) => {
				store.commit('resetUnsavedChanges');
			})
			.finally(() => store.commit('app/removeRequest', 'addCustomer', {root: true}));
	},

	/**
   * Delete Customer
   * 
   * @param {CustomersState} store 
   * @param customer Customer
   */
	 async deleteCustomer(store: ActionContext<CustomersState, any>, customer: Customer): Promise<void> {
		store.commit('app/addRequest', 'deleteCustomer', {root: true});
		return API.del('RestAPI', `/customers/${customer.id}`, {})
			.then(() => {
				store.commit('resetUnsavedChanges');
				store.commit('setCustomers', _.remove(store.state.list, (c: Customer) => c.id != customer.id));
			})
			.finally(() => store.commit('app/removeRequest', 'deleteCustomer', {root: true}));
	},


	/**
   * Update given Customer details
   * 
   * @param {CustomersState} store 
   * @param customer Customer
   */
	async updateCustomer(store: ActionContext<CustomersState, any>, customer: Customer): Promise<void> {
		store.commit('app/addRequest', 'updateCustomer', {root: true});
		return API.put('RestAPI', `/customers/${customer.id}`, {body: customer.toJSON()})
			.then((data) => new Customer(data))
			.then((customer: Customer) => {
				store.commit('setCurrent', customer);
				store.commit('resetUnsavedChanges');
			})
			.finally(() => store.commit('app/removeRequest', 'updateCustomer', {root: true}));
	},

	/**
   * Sets any deep customer property and increments the "unsaved changes" count
   * @param store
   * @param data
   */
	 setCustomerDeep(store: ActionContext<CustomersState, any>, data): void {
		store.commit('setCustomerDeep', data);
		store.commit('addUnsavedChange');
	},

 }
